/**
 * Shared "on-job" hiring-team role options surfaced in reviewer / approver pickers.
 *
 * Two value conventions exist on the backend, so we keep two option sets:
 *  - Interview-plan activities (Review / Assessment / HackerRank / Coding) send
 *    `job_*` participant-group names (resolved via `participant_groups`).
 *  - Approvals (Job/Offer + Requisition) send `*_on_job` dynamic-category names
 *    (resolved via `ApprovalUserGroup#resolve_dynamic_users`).
 *
 * Previously every surface hardcoded these lists independently (in two key
 * shapes — `label`/`value` and `name`/`id`). Centralising them here keeps the
 * options, labels and the filtering/payload gates in sync.
 */

interface OnJobRole<V extends string> {
  value: V;
  label: string;
}

// ---- Interview-plan activities (participant_groups, `job_*` values) ----

export type InterviewPlanRoleValue =
  | 'job_coordinator'
  | 'job_recruiter'
  | 'job_hiring_manager'
  | 'job_primary_recruiter'
  | 'job_primary_hiring_manager';

export const INTERVIEW_PLAN_ON_JOB_ROLES: ReadonlyArray<
  OnJobRole<InterviewPlanRoleValue>
> = [
  { value: 'job_coordinator', label: 'Coordinators (on job)' },
  { value: 'job_recruiter', label: 'Recruiters (on job)' },
  { value: 'job_hiring_manager', label: 'Hiring Managers (on job)' },
  { value: 'job_primary_recruiter', label: 'Primary Recruiter (on job)' },
  {
    value: 'job_primary_hiring_manager',
    label: 'Primary Hiring Manager (on job)',
  },
];

/**
 * Internal-email recipients: the reviewer roles plus external recruiters,
 * worded and ordered as the recipient picker's design specifies.
 *
 * Kept separate from {@link INTERVIEW_PLAN_ON_JOB_ROLES} on purpose. The
 * backend accepts `job_sourcer` anywhere a job role is valid (it is in
 * `AtsJob::HIRING_TEAM_ROLES`, which `valid_role?` gates on), but adding it to
 * the shared list would also surface it — and this wording — in the Review,
 * automation-assignee and scorecard-threshold pickers, which nobody asked for.
 *
 * The values are the same `job_*` participant groups either way; only the
 * labels and their order differ. "All external recruiters" resolves to whoever
 * is in "External recruiters (agency)" on the job's Hiring team tab, which
 * writes the same `job_sourcer` role.
 */
export type InternalEmailRoleValue = InterviewPlanRoleValue | 'job_sourcer';

interface InternalEmailOnJobRole extends OnJobRole<InternalEmailRoleValue> {
  /**
   * The role as a sentence names it — "All users with the <roleName> role on
   * this job" — which the label cannot supply: it is design copy ("All hiring
   * managers", "Primary recruiters") and building the sentence from it doubled
   * the "All" and capitalised mid-sentence.
   */
  roleName: string;
}

export const INTERNAL_EMAIL_ON_JOB_ROLES: ReadonlyArray<InternalEmailOnJobRole> =
  [
    {
      value: 'job_primary_hiring_manager',
      label: 'Primary hiring managers (on job)',
      roleName: 'primary hiring managers',
    },
    {
      value: 'job_hiring_manager',
      label: 'All hiring managers (on job)',
      roleName: 'hiring managers',
    },
    {
      value: 'job_primary_recruiter',
      label: 'Primary recruiters (on job)',
      roleName: 'primary recruiters',
    },
    {
      value: 'job_recruiter',
      label: 'All recruiters (on job)',
      roleName: 'recruiters',
    },
    {
      value: 'job_coordinator',
      label: 'All coordinators (on job)',
      roleName: 'coordinators',
    },
    {
      value: 'job_sourcer',
      label: 'All external recruiters (on job)',
      roleName: 'external recruiters',
    },
  ];

export const INTERNAL_EMAIL_ON_JOB_ROLE_VALUES: ReadonlySet<string> = new Set(
  INTERNAL_EMAIL_ON_JOB_ROLES.map((role) => role.value)
);

/** What a job-role chip says on hover: who the token resolves to, on this job. */
export const internalEmailJobRoleTooltip = (value: string): string | null => {
  const role = INTERNAL_EMAIL_ON_JOB_ROLES.find(
    (entry) => entry.value === value
  );
  return role ? `All users with the ${role.roleName} role on this job` : null;
};

/** Set of `job_*` values used to split reviewer ids into groups vs individual users. */
export const INTERVIEW_PLAN_ON_JOB_ROLE_VALUES: ReadonlySet<string> = new Set(
  INTERVIEW_PLAN_ON_JOB_ROLES.map((role) => role.value)
);

/** `label`/`value` option shape (assessment, hackerrank, coding-assessment pickers). */
export const interviewPlanRoleOptions = INTERVIEW_PLAN_ON_JOB_ROLES.map(
  (role) => ({ label: role.label, value: role.value, hideAvatar: true })
);

/** `name`/`id` option shape (Review picker). */
export const interviewPlanRoleOptionsByName = INTERVIEW_PLAN_ON_JOB_ROLES.map(
  (role) => ({ name: role.label, id: role.value, hideAvatar: true })
);

// ---- Approvals (dynamic categories, `*_on_job` values) ----

// Approval reviewer pickers (job / offer) intentionally surface only the
// hiring-manager on-job roles. The backend enum also supports recruiter
// categories (`recruiters_on_job`, `primary_recruiters_on_job`), but those are
// not offered as approval reviewers, so they are deliberately omitted here.
export type ApprovalOnJobRoleValue =
  | 'hiring_managers_on_job'
  | 'primary_hiring_managers_on_job';

export const APPROVAL_ON_JOB_ROLES: ReadonlyArray<
  OnJobRole<ApprovalOnJobRoleValue>
> = [
  { value: 'hiring_managers_on_job', label: 'Hiring Manager (on job)' },
  {
    value: 'primary_hiring_managers_on_job',
    label: 'Primary Hiring Manager (on job)',
  },
];

/** Set of approval dynamic-category values — used to choose dynamic vs static group payload. */
export const APPROVAL_ON_JOB_ROLE_VALUES: ReadonlySet<string> = new Set(
  APPROVAL_ON_JOB_ROLES.map((role) => role.value)
);

/** `label`/`value` option shape for approval reviewer pickers. */
export const approvalRoleOptions = APPROVAL_ON_JOB_ROLES.map((role) => ({
  label: role.label,
  value: role.value,
  hideAvatar: true,
}));
